// The AI Research Lab's learning-project catalog. Each entry drives a guided,
// content-only walkthrough (no live execution in v1) — the figures are realistic
// benchmark values so the "beat the baseline" moment is honest.
//
// taskType decides the prototype-result framing:
//   classification → metric is accuracy %, baseline = "guess the most common class"
//   regression     → metric is R² (0–1), baseline = "always predict the average" (R²=0)
//
// scholarCategory maps the dataset to a Scholar Step-1 research category (curated_category)
// so the student sees the bridge from practice to a real project; citation grounds it in
// published work that used the dataset.

export type TaskType = 'classification' | 'regression'

export interface LabQuestion { text: string; tag: string; note: string; recommended?: boolean }
export interface LabModel { name: string; blurb: string; score: number } // score = accuracy% or R²

export interface LabProject {
  key:            string
  title:          string
  application:    string
  hook:           string
  scholarCategory: string    // a curated_category name (Scholar Step 1)
  citation:       string     // a published work that used this dataset
  citationUrl?:   string
  taskType:       TaskType
  advanced?:      boolean
  deepLearning?:  boolean    // uses a neural network (CNN/RNN/deep net) as its headline method
  dataset:        string
  datasetNote:    string
  columns?:       string[]
  baselineLabel:  string
  baseline:       number
  questions:      LabQuestion[]
  models:         LabModel[]
}

export const LAB_PROJECTS: LabProject[] = [
  {
    key: 'sentiment', title: 'Read the mood of a review', application: 'Sentiment analysis',
    taskType: 'classification',
    hook: 'Teach an AI to tell a positive review from a negative one.',
    scholarCategory: 'Behavioral & Social Sciences',
    citation: 'Kotzias, D., Denil, M., de Freitas, N., & Smyth, P. (2015). From group to individual labels using deep features. In Proceedings of the 21st ACM SIGKDD International Conference on Knowledge Discovery and Data Mining (pp. 597–606). ACM.',
    dataset: 'IMDb movie-review sentences (1,000 labeled positive or negative)',
    datasetNote: 'Each row is a short review; the words are the features, and the label is whether it was positive or negative.',
    baselineLabel: 'always guess the more common sentiment', baseline: 50,
    questions: [
      { text: 'Can we predict if a review is positive or negative from its words?', tag: 'Classification', recommended: true,
        note: 'Clean classification — target is the sentiment label, features are the words. The go-to first language project.' },
      { text: 'Which words most strongly signal a positive review?', tag: 'Feature importance',
        note: 'A nice angle once you can classify — read which words the model weights most.' },
      { text: 'Are longer reviews more positive or negative?', tag: 'Relationship',
        note: 'A simpler exploratory question about review length vs. sentiment.' },
    ],
    models: [
      { name: 'Logistic Regression (bag-of-words)', blurb: 'Turns text into word counts and weighs which words push positive vs. negative. Fast, interpretable — the language baseline.', score: 78 },
      { name: 'Naive Bayes', blurb: 'A probability model classic for text — how likely is each word in positive vs. negative reviews.', score: 76 },
      { name: 'Linear SVM', blurb: 'Finds the boundary that best separates positive from negative in word-space; often a touch stronger on text.', score: 79 },
    ],
  },
  {
    key: 'diagnosis', title: 'Diagnose from cell measurements', application: 'Tabular classification',
    taskType: 'classification',
    hook: 'Predict whether a tumor is benign or malignant from cell measurements.',
    scholarCategory: 'Biomedical & Health Sciences',
    citation: 'Street, W. N., Wolberg, W. H., & Mangasarian, O. L. (1993). Nuclear feature extraction for breast tumor diagnosis. In Biomedical Image Processing and Biomedical Visualization (Vol. 1905, pp. 861–870). SPIE.',
    dataset: 'Wisconsin Breast Cancer (569 samples, 30 features)',
    datasetNote: 'Each row is a tumor sample; the columns are cell measurements; the target is the diagnosis.',
    columns: ['radius', 'texture', 'perimeter', 'area', 'smoothness', '… (30 total)', 'diagnosis ← target'],
    baselineLabel: 'always guess the more common diagnosis', baseline: 63,
    questions: [
      { text: 'Can we predict benign vs. malignant from the cell measurements?', tag: 'Classification', recommended: true,
        note: 'The classic medical classification — target = diagnosis, features = the measurements.' },
      { text: 'Which measurements best separate benign from malignant?', tag: 'Feature importance',
        note: 'A strong angle — which of the 30 features carry the signal.' },
      { text: 'Can we match the accuracy with just a few measurements?', tag: 'Simpler model',
        note: 'A real research angle — a simpler, cheaper model that is nearly as good.' },
    ],
    models: [
      { name: 'Logistic Regression', blurb: 'Weighs each measurement toward benign or malignant. Simple and interpretable.', score: 96 },
      { name: 'Decision Tree', blurb: 'Asks yes/no questions about measurements — you can read its rules.', score: 94 },
      { name: 'K-Nearest Neighbors', blurb: 'Classifies a sample by the most similar past samples.', score: 93 },
      { name: 'Neural Network (MLP)', blurb: 'A small neural network that stacks layers to learn non-linear patterns — the gateway to deep learning. Needs scaled features and more tuning than the simple models.', score: 97 },
    ],
  },
  {
    key: 'digits', title: 'Read handwritten numbers', application: 'Image classification',
    taskType: 'classification',
    hook: 'Teach an AI to recognize handwritten digits.',
    scholarCategory: 'Computational Science & Artificial Intelligence',
    citation: 'Alpaydin, E., & Kaynak, C. (1998). Optical recognition of handwritten digits [Data set]. UCI Machine Learning Repository.',
    dataset: 'Handwritten digits (1,797 images, 8×8 pixels)',
    datasetNote: 'Each row is a tiny image; each of the 64 pixels is a feature; the target is the digit 0–9.',
    baselineLabel: 'always guess the most common digit', baseline: 10,
    questions: [
      { text: 'Can we predict which digit (0–9) a small image shows?', tag: 'Classification', recommended: true,
        note: 'Image classification with 10 classes; each pixel is a feature.' },
      { text: 'Which digits does the model confuse most?', tag: 'Error analysis',
        note: 'A great angle — study the confusion matrix (is 4 mistaken for 9?).' },
      { text: 'How few pixels can we use and still recognize the digit?', tag: 'Simpler model',
        note: 'Explore how much image detail the model actually needs.' },
    ],
    models: [
      { name: 'K-Nearest Neighbors', blurb: 'Classifies a digit by the most similar images — surprisingly strong here.', score: 99 },
      { name: 'Logistic Regression', blurb: 'Weighs each pixel toward each digit. A solid, fast baseline.', score: 96 },
      { name: 'Decision Tree', blurb: 'Splits on pixel values with yes/no rules. Interpretable but weaker on images.', score: 85 },
    ],
  },
  {
    key: 'species', title: 'Identify a species', application: 'Tabular classification',
    taskType: 'classification',
    hook: 'Identify a penguin’s species from its body measurements.',
    scholarCategory: 'Animal Sciences & Ethology',
    citation: 'Gorman, K. B., Williams, T. D., & Fraser, W. R. (2014). Ecological sexual dimorphism and environmental variability within a community of Antarctic penguins (genus Pygoscelis). PLOS ONE, 9(3), Article e90081.',
    citationUrl: 'https://doi.org/10.1371/journal.pone.0090081',
    dataset: 'Palmer Penguins (~340 penguins)',
    datasetNote: 'Each row is a penguin; the columns are body measurements; the target is the species.',
    columns: ['bill length', 'bill depth', 'flipper length', 'body mass', 'island', 'species ← target'],
    baselineLabel: 'always guess the most common species', baseline: 44,
    questions: [
      { text: 'Can we predict a penguin’s species from its measurements?', tag: 'Classification', recommended: true,
        note: 'Target = species, features = the measurements. A clean starting classification.' },
      { text: 'Do heavier penguins have longer flippers?', tag: 'Relationship',
        note: 'A different shape — a relationship between two numbers rather than a category.' },
      { text: 'Which measurement best separates the species?', tag: 'Feature importance',
        note: 'A feature-importance angle — good once you can classify.' },
    ],
    models: [
      { name: 'Logistic Regression', blurb: 'Draws straight-line boundaries between species. Simple and interpretable.', score: 99 },
      { name: 'Decision Tree', blurb: 'Yes/no questions about measurements — you can read the rules.', score: 97 },
      { name: 'K-Nearest Neighbors', blurb: 'Classifies by the most similar penguins — but sensitive to feature scaling (body mass dwarfs bill depth).', score: 78 },
    ],
  },
  {
    key: 'airquality', title: 'Predict a pollutant level', application: 'Prediction (regression)',
    taskType: 'regression',
    hook: 'Predict a pollutant’s level from other air-sensor readings.',
    scholarCategory: 'Earth & Atmospheric Sciences',
    citation: 'De Vito, S., Massera, E., Piga, M., Martinotto, L., & Di Francia, G. (2008). On field calibration of an electronic nose for benzene estimation in an urban pollution monitoring scenario. Sensors and Actuators B: Chemical, 129(2), 750–757.',
    citationUrl: 'https://doi.org/10.1016/j.snb.2007.09.060',
    dataset: 'UCI Air Quality (hourly readings from a city sensor array)',
    datasetNote: 'Each row is one hour of sensor readings; the target is a pollutant concentration — a number, so this is regression.',
    baselineLabel: 'always predict the average', baseline: 0,
    questions: [
      { text: 'Can we predict a pollutant’s level from the other sensor readings?', tag: 'Regression', recommended: true,
        note: 'Predicting a number (a concentration). The baseline is “always predict the average”, which explains none of the variation (R²=0).' },
      { text: 'Does pollution follow a daily cycle?', tag: 'Pattern / time',
        note: 'A time-based angle using the hour of day — great, a bit more advanced.' },
      { text: 'Which sensor best predicts the pollutant?', tag: 'Feature importance',
        note: 'Which input carries the most signal.' },
    ],
    models: [
      { name: 'Linear Regression', blurb: 'Fits a straight-line relationship between the inputs and the pollutant. Simple, interpretable baseline.', score: 0.72 },
      { name: 'Decision Tree Regressor', blurb: 'Splits the data with yes/no rules to predict the number — captures non-linear patterns.', score: 0.80 },
      { name: 'Random Forest Regressor', blurb: 'Averages many trees; usually more accurate and stable.', score: 0.86 },
    ],
  },
  {
    key: 'spam', title: 'Build a spam filter', application: 'Text classification',
    taskType: 'classification',
    hook: 'Flag junk messages from real ones.',
    scholarCategory: 'Computational Science & Artificial Intelligence',
    citation: 'Almeida, T. A., Gómez Hidalgo, J. M., & Yamakami, A. (2011). Contributions to the study of SMS spam filtering: New collection and results. In Proceedings of the 11th ACM Symposium on Document Engineering (pp. 259–262). ACM.',
    dataset: 'SMS Spam Collection (5,574 messages)',
    datasetNote: 'Each row is a text message; the words are the features; the target is spam or not-spam.',
    baselineLabel: 'always guess “not spam”', baseline: 87,
    questions: [
      { text: 'Can we tell spam from real messages by their words?', tag: 'Classification', recommended: true,
        note: 'Text classification. Note the baseline “always say not-spam” is already ~87% — a real bar to beat.' },
      { text: 'Which words are the biggest spam giveaways?', tag: 'Feature importance',
        note: 'Read the words the model weights toward spam.' },
      { text: 'Does message length predict spam?', tag: 'Relationship',
        note: 'A simpler exploratory question.' },
    ],
    models: [
      { name: 'Naive Bayes', blurb: 'The classic spam-filter model — how likely is each word in spam vs. real messages.', score: 98 },
      { name: 'Logistic Regression', blurb: 'Weighs each word toward spam or not. Fast and interpretable.', score: 98 },
      { name: 'Linear SVM', blurb: 'Finds the best boundary between spam and real in word-space.', score: 97 },
    ],
  },
  {
    key: 'pneumonia', title: 'Detect pneumonia from a chest X-ray', application: 'Medical image classification',
    taskType: 'classification', advanced: true,
    hook: 'Spot pneumonia in a chest X-ray.',
    scholarCategory: 'Biomedical & Health Sciences',
    citation: 'Yang, J., Shi, R., Wei, D., Liu, Z., Zhao, L., Ke, B., Pfister, H., & Ni, B. (2023). MedMNIST v2 — A large-scale lightweight benchmark for 2D and 3D biomedical image classification. Scientific Data, 10, Article 41.',
    citationUrl: 'https://doi.org/10.1038/s41597-022-01721-8',
    dataset: 'PneumoniaMNIST (chest X-rays, 28×28 — public, de-identified)',
    datasetNote: 'Each row is a small chest X-ray; each pixel is a feature; the target is pneumonia vs. normal. Public de-identified data — no patient permission needed.',
    baselineLabel: 'always guess the more common label', baseline: 62,
    questions: [
      { text: 'Can we detect pneumonia from a chest X-ray?', tag: 'Classification', recommended: true,
        note: 'Binary medical image classification. Because the data is public and de-identified, there’s no human-subjects paperwork.' },
      { text: 'Is it worse to miss pneumonia or to false-alarm?', tag: 'Error analysis',
        note: 'A vital medical angle — a missed case (false negative) is more dangerous. Study both error types, not just accuracy.' },
      { text: 'Does the model still work on blurrier images?', tag: 'Robustness',
        note: 'Test how much image detail it really needs.' },
    ],
    models: [
      { name: 'Logistic Regression (on pixels)', blurb: 'Treats each pixel as a feature. A simple, surprisingly decent baseline for small images.', score: 84 },
      { name: 'Small Convolutional Network (CNN)', blurb: 'Looks at patches of the image (edges, shapes) — the standard tool for images.', score: 90 },
      { name: 'K-Nearest Neighbors', blurb: 'Classifies an X-ray by the most similar X-rays. Intuitive but pixel-sensitive.', score: 80 },
    ],
  },
  {
    key: 'derma', title: 'Classify a skin lesion', application: 'Medical image classification',
    taskType: 'classification', advanced: true,
    hook: 'Sort a skin-lesion photo into one of seven conditions.',
    scholarCategory: 'Biomedical & Health Sciences',
    citation: 'Tschandl, P., Rosendahl, C., & Kittler, H. (2018). The HAM10000 dataset, a large collection of multi-source dermatoscopic images of common pigmented skin lesions. Scientific Data, 5, Article 180161.',
    citationUrl: 'https://doi.org/10.1038/sdata.2018.161',
    dataset: 'DermaMNIST (skin images, 7 conditions, 28×28 — from HAM10000)',
    datasetNote: 'Each row is a small skin-lesion image; the target is one of 7 conditions. Public, de-identified data.',
    baselineLabel: 'always guess the most common condition', baseline: 67,
    questions: [
      { text: 'Can we classify a skin lesion into one of 7 conditions from an image?', tag: 'Classification', recommended: true,
        note: 'Multi-class medical image classification. Watch the imbalance — one condition dominates.' },
      { text: 'Which conditions get confused with each other?', tag: 'Error analysis',
        note: 'Study the confusion matrix — which look-alikes trip the model.' },
      { text: 'Is the model just favoring the most common condition?', tag: 'Fairness',
        note: 'An important angle — with imbalanced classes, check per-class accuracy, not just the overall number.' },
    ],
    models: [
      { name: 'Logistic Regression (on pixels)', blurb: 'A simple pixel-based baseline; struggles with subtle multi-class differences.', score: 71 },
      { name: 'Small Convolutional Network (CNN)', blurb: 'Learns visual patterns in patches — the right tool for images.', score: 76 },
      { name: 'K-Nearest Neighbors', blurb: 'Classifies by the most similar images.', score: 68 },
    ],
  },
  {
    key: 'newstopic', title: 'Sort news by topic', application: 'Text classification',
    taskType: 'classification',
    hook: 'Teach an AI to tell an article’s topic from its words.',
    scholarCategory: 'Computational Science & Artificial Intelligence',
    citation: 'Lang, K. (1995). NewsWeeder: Learning to filter netnews. In Proceedings of the Twelfth International Conference on Machine Learning (pp. 331–339). Morgan Kaufmann.',
    dataset: '20 Newsgroups (news posts across topics; this project uses 4)',
    datasetNote: 'Each row is a post (free text); the words are the features, and the topic is the target.',
    baselineLabel: 'always guess the most common topic', baseline: 25,
    questions: [
      { text: 'Can we tell a post’s topic from its words?', tag: 'Classification', recommended: true,
        note: 'Multi-class text classification — the target is one of several topics, the features are the words.' },
      { text: 'Which words most strongly signal each topic?', tag: 'Feature importance',
        note: 'A nice angle — read which words the model leans on for each topic.' },
      { text: 'Which topics get confused with each other?', tag: 'Error analysis',
        note: 'Study the confusion matrix — are space and politics ever mixed up?' },
    ],
    models: [
      { name: 'Naive Bayes', blurb: 'The text classic — how likely is each word under each topic. Fast and strong on word counts.', score: 90 },
      { name: 'Linear SVM', blurb: 'Finds the boundary that best separates topics in word-space; often a touch stronger on text.', score: 92 },
      { name: 'Logistic Regression', blurb: 'Weighs each word toward each topic. Fast and interpretable.', score: 91 },
    ],
  },
  {
    key: 'faces', title: 'Recognize a face', application: 'Face recognition',
    taskType: 'classification', advanced: true,
    hook: 'Teach an AI to tell who is in a photo.',
    scholarCategory: 'Computational Science & Artificial Intelligence',
    citation: 'Huang, G. B., Ramesh, M., Berg, T., & Learned-Miller, E. (2007). Labeled Faces in the Wild: A database for studying face recognition in unconstrained environments (Technical Report 07-49). University of Massachusetts, Amherst.',
    dataset: 'Labeled Faces in the Wild (photos of public figures)',
    datasetNote: 'Each row is a face photo; the pixels are the features, and the person’s name is the target. (The first run downloads the images.)',
    baselineLabel: 'always guess the most-photographed person', baseline: 41,
    questions: [
      { text: 'Can we identify who is in a photo from the pixels?', tag: 'Classification', recommended: true,
        note: 'Face recognition as classification — target = the person, features = the pixels.' },
      { text: 'Which people get confused with each other?', tag: 'Error analysis',
        note: 'Study the confusion matrix — who looks alike to the model.' },
      { text: 'Is the model just favoring the most common person?', tag: 'Fairness',
        note: 'With imbalanced classes, check per-person accuracy, not just the overall number.' },
    ],
    models: [
      { name: 'Support Vector Machine (with PCA / eigenfaces)', blurb: 'Compresses faces to their key patterns (eigenfaces), then draws the widest boundary between people. The classic face-recognition approach.', score: 85 },
      { name: 'Logistic Regression', blurb: 'Weighs pixels toward each person. A simple, fast baseline on faces.', score: 80 },
      { name: 'K-Nearest Neighbors', blurb: 'Classifies a face by the most similar past photos — sensitive to lighting and pose.', score: 64 },
    ],
  },
  {
    key: 'cifar', title: 'Recognize everyday objects', application: 'Image classification',
    taskType: 'classification', advanced: true, deepLearning: true,
    hook: 'Teach a neural network to recognize objects — planes, cats, cars, ships.',
    scholarCategory: 'Computational Science & Artificial Intelligence',
    citation: 'Krizhevsky, A. (2009). Learning multiple layers of features from tiny images (Technical Report). University of Toronto.',
    dataset: 'CIFAR-10 (60,000 color photos, 10 object classes, 32×32)',
    datasetNote: 'Each row is a small color photo; the pixels are the features, the object class is the target. The prototype uses a subset so it runs fast.',
    baselineLabel: 'always guess the most common class', baseline: 10,
    questions: [
      { text: 'Can a neural network recognize the object in a photo?', tag: 'Classification', recommended: true,
        note: 'Deep-learning image classification with 10 classes — a CNN learns visual patterns from the pixels.' },
      { text: 'Which objects does the model confuse most?', tag: 'Error analysis',
        note: 'A great angle — is a cat ever mistaken for a dog, a truck for a car?' },
      { text: 'Does training longer (more epochs) improve accuracy?', tag: 'Model tuning',
        note: 'The core deep-learning knob — watch accuracy climb (then plateau) as you train more.' },
    ],
    models: [
      { name: 'Small Convolutional Network (CNN)', blurb: 'Scans the image in patches to learn edges → shapes → objects. The right tool for photos.', score: 65 },
      { name: 'Logistic Regression (on pixels)', blurb: 'A flat pixel-based baseline — weak on real photos, which is exactly the point.', score: 35 },
      { name: 'K-Nearest Neighbors', blurb: 'Classifies by the most similar images; struggles on varied real-world photos.', score: 30 },
    ],
  },
  {
    key: 'diabetes', title: 'Predict diabetes risk', application: 'Tabular classification',
    taskType: 'classification',
    hook: 'Predict whether someone is likely diabetic from routine health measurements.',
    scholarCategory: 'Public Health & Epidemiology',
    citation: 'Smith, J. W., Everhart, J. E., Dickson, W. C., Knowler, W. C., & Johannes, R. S. (1988). Using the ADAP learning algorithm to forecast the onset of diabetes mellitus. In Proceedings of the Annual Symposium on Computer Application in Medical Care (pp. 261–265). American Medical Informatics Association.',
    dataset: 'Pima Indians Diabetes (768 patients, 8 health measurements)',
    datasetNote: 'Each row is a patient; the columns are health measurements; the target is whether they developed diabetes.',
    columns: ['pregnancies', 'glucose', 'blood pressure', 'skin thickness', 'insulin', 'BMI', 'pedigree', 'age', 'diabetes ← target'],
    baselineLabel: 'always guess “no diabetes”', baseline: 65,
    questions: [
      { text: 'Can we predict diabetes from routine health measurements?', tag: 'Classification', recommended: true,
        note: 'A classic public-health screening question — target = diabetes, features = the measurements.' },
      { text: 'Which measurements matter most for diabetes risk?', tag: 'Feature importance',
        note: 'A strong angle — is glucose enough, or do BMI and age add signal?' },
      { text: 'Can a few cheap measurements do nearly as well?', tag: 'Simpler model',
        note: 'A real research angle — a simpler screen that is almost as accurate.' },
    ],
    models: [
      { name: 'Logistic Regression', blurb: 'Weighs each measurement toward diabetic or not. Simple, fast, interpretable.', score: 77 },
      { name: 'Decision Tree', blurb: 'Asks yes/no questions about measurements — readable rules, but overfits easily.', score: 72 },
      { name: 'K-Nearest Neighbors', blurb: 'Classifies a patient by the most similar past patients.', score: 74 },
      { name: 'Neural Network (MLP)', blurb: 'A small neural network — learns non-linear patterns among the measurements. The gateway to deep learning; needs scaled features.', score: 76 },
    ],
  },
  {
    key: 'fakenews', title: 'Spot fake news', application: 'Text classification',
    taskType: 'classification',
    hook: 'Teach an AI to tell a real news article from a fabricated one.',
    scholarCategory: 'Behavioral & Social Sciences',
    citation: 'Ahmed, H., Traore, I., & Saad, S. (2018). Detecting opinion spam and fake news using text classification. Security and Privacy, 1(1), Article e9.',
    dataset: 'Fake and Real News (ISOT) — a balanced 5,000-article subset',
    datasetNote: 'Each row is a news article (its text); the words are the features, and the label is whether it is real or fake. A subject column is kept so you can control for topic.',
    baselineLabel: 'always guess the more common label', baseline: 50,
    questions: [
      { text: 'Can we tell real news from fake news from the words?', tag: 'Classification', recommended: true,
        note: 'Text classification — target = real vs. fake, features = the words in the article.' },
      { text: 'Which words most signal that an article is fake?', tag: 'Feature importance',
        note: 'A revealing angle — read which words the model leans on.' },
      { text: 'Does it still work if we control for topic — only political articles?', tag: 'Control variable',
        note: 'The strong science move — hold the topic fixed (study only political news) so the model can’t just be learning “politics = fake”.' },
    ],
    models: [
      { name: 'Logistic Regression', blurb: 'Weighs each word toward real or fake. Fast and interpretable.', score: 95 },
      { name: 'Naive Bayes', blurb: 'The text classic — how likely is each word in real vs. fake articles.', score: 93 },
      { name: 'Linear SVM', blurb: 'Finds the boundary that best separates real from fake in word-space.', score: 96 },
    ],
  },
  {
    key: 'alzheimer', title: 'Detect Alzheimer’s from a brain scan', application: 'Medical image classification',
    taskType: 'classification', advanced: true, deepLearning: true,
    hook: 'Teach a neural network to spot the stage of Alzheimer’s in an MRI brain scan.',
    scholarCategory: 'Neuroscience & Cognitive Science',
    citation: 'Dubey, S. (2019). Alzheimer’s Dataset (4 class of images) [Data set]. Kaggle. Derived from public brain-MRI collections.',
    dataset: 'Alzheimer MRI brain scans (4 dementia stages) — a small, truncated 64×64 subset',
    datasetNote: 'Each row is a brain-MRI slice; the pixels are the features, and the target is the dementia stage (non / very-mild / mild / moderate). This is a truncated subset — enough to prove the method runs, not to get strong results.',
    baselineLabel: 'always guess the most common stage', baseline: 31,
    questions: [
      { text: 'Can a neural network detect the dementia stage from an MRI?', tag: 'Classification', recommended: true,
        note: 'Deep-learning medical imaging — a CNN learns visual patterns from the scan’s pixels.' },
      { text: 'Which stages does the model confuse most?', tag: 'Error analysis',
        note: 'Study the confusion matrix — very-mild vs. mild are the hard ones.' },
      { text: 'How much does the truncated dataset limit accuracy?', tag: 'Data size',
        note: 'A real research angle — with more scans and training, how much better could it get?' },
    ],
    models: [
      { name: 'Small Convolutional Network (CNN)', blurb: 'Scans the MRI in patches to learn the visual signatures of each stage. The right tool for images.', score: 55 },
      { name: 'Logistic Regression (on pixels)', blurb: 'A flat pixel-based baseline; weak on subtle brain differences.', score: 40 },
      { name: 'K-Nearest Neighbors', blurb: 'Classifies a scan by the most similar past scans.', score: 38 },
    ],
  },
]

// ── Teaching content: the methods, explained + a place to learn more ──────────────
// Shared so the walkthrough teaches the method (not just a one-line blurb) and links
// to an authoritative page. Derived from the model name so projects need no per-model
// wiring. Links are stable Wikipedia articles.

interface Technique { label: string; how: string; learnMore: string }

export const TECHNIQUES: Record<string, Technique> = {
  logreg: { label: 'Logistic Regression',
    how: 'Weighs each feature and combines them to estimate the probability of each class. Fast, hard to overfit, and interpretable — you can see which features push toward which class. The classic baseline classifier.',
    learnMore: 'https://en.wikipedia.org/wiki/Logistic_regression' },
  linreg: { label: 'Linear Regression',
    how: 'Fits a straight-line relationship between the inputs and a numeric target. The simplest, most interpretable way to predict a number — and the baseline any fancier regressor should beat.',
    learnMore: 'https://en.wikipedia.org/wiki/Linear_regression' },
  tree: { label: 'Decision Tree',
    how: 'Learns a flowchart of yes/no questions about the features. Very interpretable — you can read the rules — but a single tree overfits if it grows too deep.',
    learnMore: 'https://en.wikipedia.org/wiki/Decision_tree_learning' },
  forest: { label: 'Random Forest',
    how: 'Averages many decision trees, each trained on a random slice of the data. Usually more accurate and far more stable than one tree, at the cost of some interpretability.',
    learnMore: 'https://en.wikipedia.org/wiki/Random_forest' },
  knn: { label: 'K-Nearest Neighbors',
    how: 'Classifies an example by looking at the handful of most similar examples and taking a vote. Intuitive with no real "training", but sensitive to feature scaling and slow on big data.',
    learnMore: 'https://en.wikipedia.org/wiki/K-nearest_neighbors_algorithm' },
  nb: { label: 'Naive Bayes',
    how: 'Uses probability (Bayes’ rule) with a simplifying assumption that features are independent. A classic for text — fast, handles many features, and surprisingly strong on word counts.',
    learnMore: 'https://en.wikipedia.org/wiki/Naive_Bayes_classifier' },
  svm: { label: 'Support Vector Machine',
    how: 'Finds the boundary that separates the classes with the widest possible margin. Often a touch stronger than logistic regression on high-dimensional data like text.',
    learnMore: 'https://en.wikipedia.org/wiki/Support_vector_machine' },
  cnn: { label: 'Convolutional Neural Network',
    how: 'A neural network that scans an image in small patches to detect edges, shapes, and textures, building up to recognizing objects. The standard tool for images — more powerful, but needs more data and compute.',
    learnMore: 'https://en.wikipedia.org/wiki/Convolutional_neural_network' },
  mlp: { label: 'Neural Network (Multi-Layer Perceptron)',
    how: 'Stacks layers of simple "neurons" that each combine the previous layer’s outputs, learning non-linear patterns a straight line would miss. The gateway to deep learning — flexible, but hungrier for data and tuning than the classic models.',
    learnMore: 'https://en.wikipedia.org/wiki/Multilayer_perceptron' },
}

export function techniqueFor(modelName: string): Technique | null {
  const n = modelName.toLowerCase()
  if (n.includes('logistic'))                          return TECHNIQUES.logreg
  if (n.includes('linear regression'))                 return TECHNIQUES.linreg
  if (n.includes('random forest'))                     return TECHNIQUES.forest
  if (n.includes('decision tree'))                     return TECHNIQUES.tree
  if (n.includes('nearest'))                           return TECHNIQUES.knn
  if (n.includes('naive'))                             return TECHNIQUES.nb
  if (n.includes('svm') || n.includes('support vector')) return TECHNIQUES.svm
  if (n.includes('convolutional') || n.includes('cnn')) return TECHNIQUES.cnn
  if (n.includes('neural') || n.includes('mlp'))       return TECHNIQUES.mlp
  return null
}

export function applicationLearnMore(application: string): { label: string; url: string } {
  const a = application.toLowerCase()
  if (a.includes('sentiment'))  return { label: 'Sentiment analysis', url: 'https://en.wikipedia.org/wiki/Sentiment_analysis' }
  if (a.includes('regression')) return { label: 'Regression analysis', url: 'https://en.wikipedia.org/wiki/Regression_analysis' }
  if (a.includes('face') || a.includes('recognition')) return { label: 'Facial recognition', url: 'https://en.wikipedia.org/wiki/Facial_recognition_system' }
  if (a.includes('image'))      return { label: 'Computer vision', url: 'https://en.wikipedia.org/wiki/Computer_vision' }
  if (a.includes('topic') || a.includes('text')) return { label: 'Text classification', url: 'https://en.wikipedia.org/wiki/Document_classification' }
  return { label: 'Classification', url: 'https://en.wikipedia.org/wiki/Statistical_classification' }
}

// ── Dataset detail + a real 5-row sample (the "Explore the dataset" content) ──────
// Learning objective of the first step: seeing that a dataset is EXAMPLES (rows) ×
// FEATURES (columns) + a target — you pick features and use many examples to train
// a model. Tabular/text sets show real sample rows; image sets explain the pixels.

export interface DatasetSample { columns: string[]; rows: (string | number)[][]; note?: string }
// sampleImages = how many real example thumbnails exist under
// public/lab-samples/<project key>/1..N.png (generated by scripts/gen_lab_samples.py).
// sampleCode = a copy-paste Colab snippet to view a few examples, for image
// datasets too big to host (they download in Colab anyway — e.g. CIFAR-10).
export interface DatasetDetail { full: string; sample?: DatasetSample; imageNote?: string; sampleImages?: number; sampleCode?: string }

export const DATASET_DETAIL: Record<string, DatasetDetail> = {
  diagnosis: {
    full: 'The Wisconsin Breast Cancer dataset has 569 tumor samples. For each, a digitized image of a fine-needle aspirate was analyzed to measure 30 numeric features of the cell nuclei — radius, texture, perimeter, area, smoothness and more. Every sample is labeled by a physician as benign or malignant. A standard benchmark for medical classification.',
    sample: {
      columns: ['radius', 'texture', 'perimeter', 'area', 'smoothness', 'diagnosis ← target'],
      rows: [
        [17.99, 10.38, 122.8, 1001, 0.118, 'malignant'],
        [13.54, 14.36, 87.46, 566.3, 0.098, 'benign'],
        [20.29, 14.34, 135.1, 1297, 0.100, 'malignant'],
        [12.45, 15.70, 82.57, 477.1, 0.128, 'benign'],
        [19.69, 21.25, 130.0, 1203, 0.110, 'malignant'],
      ],
      note: '(5 of 569 rows · 5 of 30 features shown)',
    },
  },
  species: {
    full: 'The Palmer Penguins dataset has measurements for ~344 penguins of three species (Adelie, Chinstrap, Gentoo) across three Antarctic islands. Each row records bill length and depth, flipper length, body mass, island and sex, plus the species. A clean, friendly alternative to the classic iris dataset.',
    sample: {
      columns: ['bill length (mm)', 'bill depth (mm)', 'flipper (mm)', 'body mass (g)', 'island', 'species ← target'],
      rows: [
        [39.1, 18.7, 181, 3750, 'Torgersen', 'Adelie'],
        [39.5, 17.4, 186, 3800, 'Torgersen', 'Adelie'],
        [46.5, 17.9, 192, 3500, 'Dream', 'Chinstrap'],
        [49.1, 14.8, 220, 5150, 'Biscoe', 'Gentoo'],
        [50.0, 15.2, 218, 5700, 'Biscoe', 'Gentoo'],
      ],
      note: '(5 of ~344 rows)',
    },
  },
  airquality: {
    full: 'The UCI Air Quality dataset holds ~9,350 hourly readings from a gas multi-sensor array in an Italian city over one year. Each row has several low-cost metal-oxide sensor signals plus temperature and humidity; the targets are true pollutant concentrations from a reference analyzer. Good for predicting a pollutant from cheap sensors.',
    sample: {
      columns: ['CO sensor', 'NOx sensor', 'temp (°C)', 'humidity (%)', 'benzene µg/m³ ← target'],
      rows: [
        [1360, 1046, 13.6, 48.9, 11.9],
        [1292, 955, 13.3, 47.7, 9.4],
        [1402, 939, 11.9, 54.0, 9.0],
        [1376, 948, 11.0, 60.0, 9.2],
        [1272, 836, 11.2, 59.6, 6.5],
      ],
      note: '(5 of ~9,350 hourly rows)',
    },
  },
  sentiment: {
    full: 'A set of 1,000 IMDb movie-review sentences (500 positive, 500 negative), each hand-labeled by sentiment. Each row is one short review — free text — and its label. A clean, fast starter set for text classification.',
    sample: {
      columns: ['text (the review)', 'label ← target'],
      rows: [
        ['“A wonderful little production. The filming is warm and inviting…”', 'positive'],
        ['“Painfully slow — I nearly fell asleep before the halfway mark.”', 'negative'],
        ['“One of the best films I’ve seen in years. Superb acting.”', 'positive'],
        ['“Terrible script and wooden acting. A complete waste of time.”', 'negative'],
        ['“Charming, funny, and surprisingly moving. Highly recommend.”', 'positive'],
      ],
      note: '(5 of 1,000 reviews · illustrative)',
    },
  },
  spam: {
    full: 'The SMS Spam Collection is 5,574 English text messages, each labeled spam or ham (legitimate). Short and informal, collected for spam-filtering research.',
    sample: {
      columns: ['message (text)', 'label ← target'],
      rows: [
        ['“Free entry in 2 a wkly comp to win FA Cup final tkts — text WIN…”', 'spam'],
        ['“Nah I don’t think he goes to usf, he lives around here though.”', 'ham'],
        ['“WINNER!! You have been selected to receive a £900 prize. Call now.”', 'spam'],
        ['“Ok lar... Joking wif u oni...”', 'ham'],
        ['“Sorry, I’ll call later.”', 'ham'],
      ],
      note: '(5 of 5,574 messages)',
    },
  },
  digits: {
    full: 'The Optical Recognition of Handwritten Digits dataset has 1,797 images of digits 0–9. Each image is 8×8 pixels, so it’s described by 64 numbers — the brightness of each pixel (0–16). The target is which digit it shows. A small, fast image dataset.',
    imageNote: 'Each example is an 8×8 image — 64 pixel-brightness values. The pixels are the features and the digit is the target; you feed the model the pixels rather than hand-picked columns.',
    sampleImages: 6,
  },
  pneumonia: {
    full: 'PneumoniaMNIST (from MedMNIST) is 5,856 chest X-ray images resized to 28×28, each labeled pneumonia or normal. Public and de-identified, so no patient permission is needed.',
    imageNote: 'Each example is a 28×28 X-ray image — 784 pixel values. The pixels are the features and the diagnosis is the target.',
    sampleImages: 6,
  },
  derma: {
    full: 'DermaMNIST (from MedMNIST, built on HAM10000) is 10,015 dermatoscopic images resized to 28×28, each labeled with one of 7 skin conditions. Public and de-identified.',
    imageNote: 'Each example is a 28×28 color image — pixel values across 3 channels. The pixels are the features and the condition is the target.',
    sampleImages: 6,
  },
  newstopic: {
    full: 'The 20 Newsgroups collection has ~18,000 forum posts sorted into 20 topics. This project uses 4 clearly-different topics (computer graphics, space, religion, politics) so it runs fast and the classes are easy to tell apart. Each row is a post’s text and its topic.',
    sample: {
      columns: ['post (text)', 'topic ← target'],
      rows: [
        ['“The new GPU renders shadows in real time using ray tracing…”', 'comp.graphics'],
        ['“The rover’s trajectory to Mars depends on the launch window…”', 'sci.space'],
        ['“Grace and faith are central themes in the epistle…”', 'soc.religion.christian'],
        ['“The senate vote on the new bill was split along party lines…”', 'talk.politics.misc'],
        ['“Anti-aliasing smooths the jagged edges in the rendered image…”', 'comp.graphics'],
      ],
      note: '(illustrative — real posts are longer)',
    },
  },
  faces: {
    full: 'Labeled Faces in the Wild (LFW) is a collection of face photos of public figures collected from the web. This project keeps the people with the most photos, so there are enough examples per person to learn from. Each image is greyscale pixels; the target is the person.',
    imageNote: 'Each example is a face photo flattened into pixel values. The pixels are the features and the person is the target. (SVM first compresses them to “eigenfaces” — the key patterns that distinguish faces.)',
    sampleImages: 6,
  },
  cifar: {
    full: 'CIFAR-10 is 60,000 tiny (32×32) color photos across 10 everyday classes — airplane, car, bird, cat, deer, dog, frog, horse, ship, truck. A classic computer-vision benchmark. The prototype trains on a subset so it finishes quickly.',
    imageNote: 'Each example is a 32×32 color photo — pixel values across 3 channels. The pixels are the features and the object class is the target. A CNN learns visual patterns directly from these pixels.',
    sampleCode: `# Run in Colab to see a few CIFAR-10 examples
from tensorflow.keras.datasets import cifar10
import matplotlib.pyplot as plt

(x_train, y_train), _ = cifar10.load_data()
classes = ['airplane','car','bird','cat','deer','dog','frog','horse','ship','truck']

plt.figure(figsize=(9, 2))
for i in range(6):
    plt.subplot(1, 6, i + 1)
    plt.imshow(x_train[i])
    plt.title(classes[int(y_train[i])])
    plt.axis('off')
plt.show()`,
  },
  diabetes: {
    full: 'The Pima Indians Diabetes dataset has 768 patients, each described by 8 routine health measurements (glucose, blood pressure, BMI, age, and more). The target is whether the patient developed diabetes. A classic, small public-health dataset.',
    sample: {
      columns: ['glucose', 'BMI', 'age', '… (8 total)', 'diabetes ← target'],
      rows: [
        [148, 33.6, 50, '…', 1],
        [85, 26.6, 31, '…', 0],
        [183, 23.3, 32, '…', 1],
        [89, 28.1, 21, '…', 0],
        [137, 43.1, 33, '…', 1],
      ],
      note: '(5 of 768 patients · a few columns shown)',
    },
  },
  alzheimer: {
    full: 'A small, truncated subset of an Alzheimer MRI dataset — brain-scan slices labeled with one of 4 dementia stages (non, very-mild, mild, moderate), resized to 64×64 greyscale. Deliberately small so it runs fast; expect modest accuracy and treat it as a proof the method works, not a strong result.',
    imageNote: 'Each example is a 64×64 greyscale MRI slice — 4,096 pixel values. The pixels are the features and the dementia stage is the target. A CNN learns the visual signatures directly from the pixels.',
    sampleImages: 6,
  },
  fakenews: {
    full: 'A balanced 5,000-article subset of the ISOT Fake and Real News dataset — real articles from Reuters and fabricated articles from flagged sources, each labeled real or fake. Each row is the article text and a subject (e.g. politicsNews, worldnews) you can filter on.',
    sample: {
      columns: ['text (the article)', 'subject', 'label ← target'],
      rows: [
        ['“WASHINGTON (Reuters) - The head of a conservative Republican faction…”', 'politicsNews', 'real'],
        ['“Donald Trump just couldn’t wish all Americans a Happy New Year…”', 'News', 'fake'],
        ['“BRUSSELS (Reuters) - NATO allies on Tuesday welcomed…”', 'worldnews', 'real'],
        ['“The number of cases of cops brutalizing and killing people…”', 'News', 'fake'],
        ['“LONDON (Reuters) - Prime Minister’s office confirmed…”', 'politicsNews', 'real'],
      ],
      note: '(illustrative — real articles are longer)',
    },
  },
}

// The hypothesis variables, per project, for the explicit IV / DV / CV framing:
//   features (IV) — 2–3 concrete features from the data
//   dv          — the target
//   cv          — feature values used to focus on a subset of the data
//   hypothesis  — the IV↔DV claim for the recommended question (no "predict")
export const HYPOTHESIS_VARS: Record<string, {
  features: string[]; dv: string; cv: string; hypothesis: string
}> = {
  sentiment: {
    features: ['the words in the review (as word counts)'],
    dv: 'whether a review is positive or negative',
    cv: 'for example, study only short reviews',
    hypothesis: 'The words in a review can tell us whether it is positive or negative.',
  },
  diagnosis: {
    features: ['radius', 'texture', 'perimeter'],
    dv: 'whether a tumor is benign or malignant',
    cv: 'for example, study only larger tumors (area above the median)',
    hypothesis: 'A tumor’s radius, texture, and perimeter can tell us whether it is benign or malignant.',
  },
  digits: {
    features: ['the 64 pixel-brightness values'],
    dv: 'which digit an image shows',
    cv: 'for example, study only the digits 3 vs. 8',
    hypothesis: 'The pattern of pixel brightness in an image can tell us which digit it shows.',
  },
  species: {
    features: ['bill length', 'flipper length', 'body mass'],
    dv: 'a penguin’s species',
    cv: 'for example, study only penguins from one island',
    hypothesis: 'A penguin’s bill length, flipper length, and body mass can tell us its species.',
  },
  airquality: {
    features: ['the CO sensor', 'the NOx sensor', 'temperature'],
    dv: 'the pollutant level',
    cv: 'for example, study only night-time hours',
    hypothesis: 'The CO and NOx sensor readings and the temperature can tell us the pollutant level.',
  },
  spam: {
    features: ['the words in the message (as word counts)'],
    dv: 'whether a message is spam',
    cv: 'for example, study only messages that contain a link',
    hypothesis: 'The words in a message can tell us whether it is spam.',
  },
  pneumonia: {
    features: ['the X-ray’s pixel values'],
    dv: 'whether an X-ray shows pneumonia',
    cv: 'for example, study only images from one machine',
    hypothesis: 'The pattern of pixels in a chest X-ray can tell us whether it shows pneumonia.',
  },
  derma: {
    features: ['the image’s pixel values'],
    dv: 'which skin condition a lesion is',
    cv: 'for example, study only one lesion type versus the rest',
    hypothesis: 'The pattern of pixels in a lesion image can tell us which skin condition it is.',
  },
  newstopic: {
    features: ['the words in the post (as word importances)'],
    dv: 'which topic a post belongs to',
    cv: 'for example, study only posts longer than 100 words',
    hypothesis: 'The words in a post can tell us which topic it belongs to.',
  },
  faces: {
    features: ['the face photo’s pixel values'],
    dv: 'which person a photo shows',
    cv: 'for example, study only clear, front-facing photos',
    hypothesis: 'The pattern of pixels in a face photo can tell us which person it shows.',
  },
  cifar: {
    features: ['the photo’s color pixel values'],
    dv: 'which object a photo shows',
    cv: 'for example, study only vehicles versus animals',
    hypothesis: 'The pattern of pixels in a photo can tell us which object it shows.',
  },
  diabetes: {
    features: ['glucose', 'BMI', 'age'],
    dv: 'whether a person develops diabetes',
    cv: 'for example, study only patients over 40',
    hypothesis: 'A person’s glucose, BMI, and age can tell us whether they are likely to develop diabetes.',
  },
  fakenews: {
    features: ['the words in the article (as word counts)'],
    dv: 'whether an article is real or fake',
    cv: 'for example, study only political articles (subject = politics), so topic is held fixed',
    hypothesis: 'The words in a news article can tell us whether it is real or fake.',
  },
  alzheimer: {
    features: ['the MRI’s pixel values'],
    dv: 'which dementia stage a brain scan shows',
    cv: 'for example, study only the clearest, well-aligned scans',
    hypothesis: 'The pattern of pixels in a brain MRI can tell us the dementia stage.',
  },
}

// ── Runnable scikit-learn code for the prototype step ─────────────────────────────
// Real, paste-into-Colab code composed from a per-dataset loader + the student's
// chosen model. Every project is wired across three paths: built-in sklearn/seaborn
// datasets and hosted text CSVs (classification), a hosted CSV (regression), and
// hosted MedMNIST .npz image sets (sklearn-on-pixels or a small Keras CNN). Datasets
// that aren't built into a library live in ArdentLabs/scholar-datasets.
const DATA_BASE = 'https://raw.githubusercontent.com/ArdentLabs/scholar-datasets/main'

const MODEL_SKLEARN: Record<string, { imp: string; ctor: string }> = {
  logreg: { imp: 'from sklearn.linear_model import LogisticRegression', ctor: 'LogisticRegression(max_iter=5000)' },
  tree:   { imp: 'from sklearn.tree import DecisionTreeClassifier',      ctor: 'DecisionTreeClassifier(random_state=0)' },
  knn:    { imp: 'from sklearn.neighbors import KNeighborsClassifier',   ctor: 'KNeighborsClassifier()' },
  nb:     { imp: 'from sklearn.naive_bayes import MultinomialNB',        ctor: 'MultinomialNB()' },
  svm:    { imp: 'from sklearn.svm import LinearSVC',                    ctor: 'LinearSVC()' },
  mlp:    { imp: 'from sklearn.neural_network import MLPClassifier',     ctor: 'MLPClassifier(hidden_layer_sizes=(64, 32), max_iter=1000, random_state=0)' },
}

// Per dataset: the extra import(s) it needs, plus the load + feature-select body.
// `vectorize` datasets are text: X is raw strings, turned into word-count features
// inside a pipeline (so the vectorizer fits on the training split only — no leakage).
const DATASET_LOAD: Record<string, { imports: string[]; body: string; vectorize?: boolean }> = {
  diagnosis: {
    imports: ['from sklearn.datasets import load_breast_cancer'],
    body:
`# 1. Load the dataset (built into scikit-learn)
data = load_breast_cancer()

# 2. Select the features (X) and the target (y)
X, y = data.data, data.target`,
  },
  digits: {
    imports: ['from sklearn.datasets import load_digits'],
    body:
`# 1. Load the dataset (built into scikit-learn)
data = load_digits()

# 2. Select the features (X) and the target (y)
X, y = data.data, data.target`,
  },
  species: {
    imports: ['import seaborn as sns'],
    body:
`# 1. Load the dataset (Palmer Penguins, via seaborn)
df = sns.load_dataset("penguins").dropna()

# 2. Select the features (X) and the target (y)
X = df[["bill_length_mm", "bill_depth_mm", "flipper_length_mm", "body_mass_g"]]
y = df["species"]`,
  },
  spam: {
    imports: ['import pandas as pd'],
    vectorize: true,
    body:
`# 1. Load the dataset (a hosted CSV of messages labeled spam or not)
url = "${DATA_BASE}/spam.csv"
df = pd.read_csv(url).dropna()

# 2. Select the features (X = the message text) and the target (y = spam or not)
X = df["text"]
y = df["type"]`,
  },
  sentiment: {
    imports: ['import pandas as pd'],
    vectorize: true,
    body:
`# 1. Load the dataset (a hosted CSV of movie-review sentences, positive or negative)
url = "${DATA_BASE}/sentiment.csv"
df = pd.read_csv(url).dropna()

# 2. Select the features (X = the review text) and the target (y = positive or negative)
X = df["text"]
y = df["label"]`,
  },
  diabetes: {
    imports: ['import pandas as pd'],
    body:
`# 1. Load the dataset (a hosted CSV of patient health measurements)
url = "${DATA_BASE}/diabetes.csv"
df = pd.read_csv(url)

# 2. Select the features (X) and the target (y = developed diabetes)
X = df[["glucose", "bmi", "age"]]
y = df["outcome"]`,
  },
  fakenews: {
    imports: ['import pandas as pd'],
    vectorize: true,
    body:
`# 1. Load the dataset (a hosted CSV of real and fake news articles)
url = "${DATA_BASE}/fakenews.csv"
df = pd.read_csv(url).dropna()
# To control for topic, keep only political articles (uncomment):
# df = df[df["subject"].str.contains("politic", case=False)]

# 2. Select the features (X = the article text) and the target (y = real or fake)
X = df["text"]
y = df["label"]`,
  },
}

function techniqueKeyFor(modelName: string): string | null {
  const n = modelName.toLowerCase()
  if (n.includes('logistic'))      return 'logreg'
  if (n.includes('decision tree')) return 'tree'
  if (n.includes('nearest'))       return 'knn'
  if (n.includes('naive bayes'))   return 'nb'
  if (n.includes('svm') || n.includes('support vector')) return 'svm'
  if (n.includes('neural') || n.includes('mlp'))         return 'mlp'
  return null
}

// Regression (hosted CSV): a regressor + R² scoring + a mean baseline.
const REGRESSOR_SKLEARN: Record<string, { imp: string; ctor: string }> = {
  linreg: { imp: 'from sklearn.linear_model import LinearRegression',    ctor: 'LinearRegression()' },
  rtree:  { imp: 'from sklearn.tree import DecisionTreeRegressor',       ctor: 'DecisionTreeRegressor(random_state=0)' },
  forest: { imp: 'from sklearn.ensemble import RandomForestRegressor',   ctor: 'RandomForestRegressor(random_state=0)' },
}
const REGRESSION_LOAD: Record<string, { file: string; features: string[]; target: string }> = {
  airquality: { file: 'airquality.csv', features: ['co_sensor', 'nox_sensor', 'temp'], target: 'benzene' },
}
function regressorKeyFor(modelName: string): string | null {
  const n = modelName.toLowerCase()
  if (n.includes('linear')) return 'linreg'
  if (n.includes('forest')) return 'forest'
  if (n.includes('tree'))   return 'rtree'
  return null
}
function regressionCode(projectKey: string, modelName: string): string | null {
  const r = REGRESSION_LOAD[projectKey]
  const k = regressorKeyFor(modelName)
  const m = k ? REGRESSOR_SKLEARN[k] : null
  if (!r || !m) return null
  const feats = r.features.map(f => `"${f}"`).join(', ')
  return `# Imports
import pandas as pd
from sklearn.model_selection import train_test_split
${m.imp}
from sklearn.dummy import DummyRegressor

# 1. Load the dataset (hosted CSV of hourly air-quality sensor readings)
url = "${DATA_BASE}/${r.file}"
df = pd.read_csv(url)

# 2. Select the features (X = sensor readings) and the target (y = the pollutant level)
X = df[[${feats}]]
y = df["${r.target}"]

# 3. Split into training and validation sets
X_train, X_val, y_train, y_val = train_test_split(X, y, test_size=0.2, random_state=0)

# 4. Run the model (train it on the training set)
model = ${m.ctor}
model.fit(X_train, y_train)

# 5. Get the results (R2 on the held-out set — 1.0 is perfect, 0 is no better than the average)
print("R2:", round(model.score(X_val, y_val), 3))

# Baseline: always predict the average value
dummy = DummyRegressor(strategy="mean").fit(X_train, y_train)
print("Baseline R2:", round(dummy.score(X_val, y_val), 3))`
}

// Image (hosted MedMNIST .npz, already split into train/val): flatten to pixels for
// sklearn (logreg/knn), or a small Keras CNN for the CNN option.
const IMAGE_LOAD: Record<string, { file: string; channels: number; classes: number }> = {
  pneumonia: { file: 'pneumoniamnist.npz', channels: 1, classes: 2 },
  derma:     { file: 'dermamnist.npz',     channels: 3, classes: 7 },
  alzheimer: { file: 'alzheimer.npz',      channels: 1, classes: 4 },
}
function imageCode(projectKey: string, modelName: string): string | null {
  const im = IMAGE_LOAD[projectKey]
  if (!im) return null
  const url = `${DATA_BASE}/${im.file}`
  const loadImgs =
`# 1. Load the dataset (hosted image set, .npz — already split into train/val)
urllib.request.urlretrieve("${url}", "data.npz")
d = np.load("data.npz")`
  if (/cnn|convolut/i.test(modelName)) {
    const channelFix = im.channels === 1
      ? 'X_train = X_train[..., None]; X_val = X_val[..., None]   # add a channel dimension\n' : ''
    return `# Imports
import urllib.request
import numpy as np
from tensorflow.keras import layers, models

${loadImgs}

# 2. Scale pixels to 0-1 and get the labels
X_train = d["train_images"].astype("float32") / 255.0
X_val   = d["val_images"].astype("float32") / 255.0
y_train = d["train_labels"].ravel()
y_val   = d["val_labels"].ravel()
${channelFix}
# 3. Build a small convolutional network
model = models.Sequential([
    layers.Input(X_train.shape[1:]),
    layers.Conv2D(16, 3, activation="relu"), layers.MaxPooling2D(),
    layers.Conv2D(32, 3, activation="relu"), layers.MaxPooling2D(),
    layers.Flatten(),
    layers.Dense(64, activation="relu"),
    layers.Dense(${im.classes}, activation="softmax"),
])
model.compile(optimizer="adam", loss="sparse_categorical_crossentropy", metrics=["accuracy"])

# 4. Train it (a few passes over the data)
model.fit(X_train, y_train, validation_data=(X_val, y_val), epochs=5, batch_size=64)

# 5. Get the results (accuracy on the held-out validation set)
print("Accuracy:", round(model.evaluate(X_val, y_val, verbose=0)[1], 3))`
  }
  const k = techniqueKeyFor(modelName)
  const m = k ? MODEL_SKLEARN[k] : null
  if (!m) return null
  return `# Imports
import urllib.request
import numpy as np
${m.imp}
from sklearn.dummy import DummyClassifier

${loadImgs}

# 2. Flatten each image into a row of pixel features; get the labels
X_train = d["train_images"].reshape(len(d["train_images"]), -1)
X_val   = d["val_images"].reshape(len(d["val_images"]), -1)
y_train = d["train_labels"].ravel()
y_val   = d["val_labels"].ravel()

# 3. (the dataset is already split into training and validation sets)

# 4. Run the model (train it on the training set)
model = ${m.ctor}
model.fit(X_train, y_train)

# 5. Get the results (accuracy on the held-out validation set)
print("Accuracy:", round(model.score(X_val, y_val), 3))

# Baseline: always guess the most common class
dummy = DummyClassifier(strategy="most_frequent").fit(X_train, y_train)
print("Baseline:", round(dummy.score(X_val, y_val), 3))`
}

// Multi-class text from a built-in sklearn corpus (20 Newsgroups, 4 topics).
function builtinTextCode(modelName: string): string | null {
  const k = techniqueKeyFor(modelName)
  const m = k ? MODEL_SKLEARN[k] : null
  if (!m) return null
  return `# Imports
from sklearn.datasets import fetch_20newsgroups
from sklearn.feature_extraction.text import TfidfVectorizer
from sklearn.pipeline import make_pipeline
${m.imp}
from sklearn.dummy import DummyClassifier

# 1. Load the dataset (4 topics from the 20 Newsgroups collection)
cats = ["comp.graphics", "sci.space", "soc.religion.christian", "talk.politics.misc"]
train = fetch_20newsgroups(subset="train", categories=cats, remove=("headers", "footers", "quotes"))
val   = fetch_20newsgroups(subset="test",  categories=cats, remove=("headers", "footers", "quotes"))
X_train, y_train = train.data, train.target
X_val,   y_val   = val.data,   val.target

# 2. Turn each post into word-importance features, then train the model
#    (the vectorizer is inside the pipeline, so it learns only from the training set)
model = make_pipeline(TfidfVectorizer(), ${m.ctor})
model.fit(X_train, y_train)

# 3. Get the results (accuracy on the held-out test set)
print("Accuracy:", round(model.score(X_val, y_val), 3))

# Baseline: always guess the most common topic
dummy = DummyClassifier(strategy="most_frequent").fit(X_train, y_train)
print("Baseline:", round(dummy.score(X_val, y_val), 3))`
}

// Face recognition from a built-in sklearn corpus (Labeled Faces in the Wild).
// SVM uses PCA "eigenfaces"; logreg/knn run on the flattened pixels directly.
function facesCode(modelName: string): string | null {
  const n = modelName.toLowerCase()
  const load =
`# 1. Load the dataset (faces of public figures; the first run downloads them)
faces = fetch_lfw_people(min_faces_per_person=70, resize=0.4)
X, y = faces.data, faces.target   # X is already flattened pixel rows

# 2. Split into training and validation sets
X_train, X_val, y_train, y_val = train_test_split(X, y, test_size=0.25, random_state=0)`
  const tail =
`# 5. Get the results (accuracy on the held-out validation set)
print("Accuracy:", round(model.score(X_val, y_val), 3))

# Baseline: always guess the most-photographed person
dummy = DummyClassifier(strategy="most_frequent").fit(X_train, y_train)
print("Baseline:", round(dummy.score(X_val, y_val), 3))`
  if (n.includes('svm') || n.includes('support vector')) {
    return `# Imports
from sklearn.datasets import fetch_lfw_people
from sklearn.model_selection import train_test_split
from sklearn.decomposition import PCA
from sklearn.svm import SVC
from sklearn.pipeline import make_pipeline
from sklearn.dummy import DummyClassifier

${load}

# 3/4. Compress faces to "eigenfaces" (PCA), then train an SVM
model = make_pipeline(PCA(n_components=150, whiten=True, random_state=0),
                      SVC(kernel="rbf", class_weight="balanced"))
model.fit(X_train, y_train)

${tail}`
  }
  const k = techniqueKeyFor(modelName)
  const m = k ? MODEL_SKLEARN[k] : null
  if (!m) return null
  return `# Imports
from sklearn.datasets import fetch_lfw_people
from sklearn.model_selection import train_test_split
${m.imp}
from sklearn.dummy import DummyClassifier

${load}

# 3/4. Run the model (train it on the training set)
model = ${m.ctor}
model.fit(X_train, y_train)

${tail}`
}

// Object recognition from a built-in Keras corpus (CIFAR-10). CNN for the deep path;
// logreg/knn flatten the pixels. Both take a SUBSET so the prototype runs fast.
function cifarCode(modelName: string): string | null {
  if (/cnn|convolut/i.test(modelName)) {
    return `# Imports
from tensorflow.keras import layers, models
from tensorflow.keras.datasets import cifar10

# 1. Load CIFAR-10 and take a SUBSET so the prototype runs fast
(X_train, y_train), (X_val, y_val) = cifar10.load_data()
X_train, y_train = X_train[:10000] / 255.0, y_train[:10000].ravel()
X_val,   y_val   = X_val[:2000] / 255.0,   y_val[:2000].ravel()

# 2. Build a small convolutional network
model = models.Sequential([
    layers.Input((32, 32, 3)),
    layers.Conv2D(32, 3, activation="relu"), layers.MaxPooling2D(),
    layers.Conv2D(64, 3, activation="relu"), layers.MaxPooling2D(),
    layers.Flatten(),
    layers.Dense(64, activation="relu"),
    layers.Dense(10, activation="softmax"),
])
model.compile(optimizer="adam", loss="sparse_categorical_crossentropy", metrics=["accuracy"])

# 3. Train briefly — a PROTOTYPE: a few epochs on a subset, just to prove the method runs.
#    A real experiment uses the full data, more epochs, and tuning to push accuracy higher.
model.fit(X_train, y_train, validation_data=(X_val, y_val), epochs=3, batch_size=64)

# 4. Get the results (accuracy on the held-out validation set)
print("Accuracy:", round(model.evaluate(X_val, y_val, verbose=0)[1], 3))`
  }
  const k = techniqueKeyFor(modelName)
  const m = k ? MODEL_SKLEARN[k] : null
  if (!m) return null
  return `# Imports
from tensorflow.keras.datasets import cifar10
${m.imp}
from sklearn.dummy import DummyClassifier

# 1. Load CIFAR-10 and take a SUBSET so it runs fast
(X_train, y_train), (X_val, y_val) = cifar10.load_data()
X_train = X_train[:10000].reshape(10000, -1); y_train = y_train[:10000].ravel()
X_val   = X_val[:2000].reshape(2000, -1);    y_val   = y_val[:2000].ravel()

# 2. Run the model (train it on the training set)
model = ${m.ctor}
model.fit(X_train, y_train)

# 3. Get the results (accuracy on the held-out validation set)
print("Accuracy:", round(model.score(X_val, y_val), 3))

# Baseline: always guess the most common class
dummy = DummyClassifier(strategy="most_frequent").fit(X_train, y_train)
print("Baseline:", round(dummy.score(X_val, y_val), 3))`
}

// Full runnable snippet for (dataset, chosen model), or null if not wired.
// All imports are collected at the top so it runs top-to-bottom with nothing missing.
export function buildColabCode(projectKey: string, modelName: string): string | null {
  if (projectKey === 'newstopic')  return builtinTextCode(modelName)
  if (projectKey === 'faces')      return facesCode(modelName)
  if (projectKey === 'cifar')      return cifarCode(modelName)
  if (REGRESSION_LOAD[projectKey]) return regressionCode(projectKey, modelName)
  if (IMAGE_LOAD[projectKey])      return imageCode(projectKey, modelName)
  const load = DATASET_LOAD[projectKey]
  const key = techniqueKeyFor(modelName)
  const m = key ? MODEL_SKLEARN[key] : null
  if (!load || !m) return null
  // A neural net (MLP) on tabular data wants scaled features, so wrap it in a pipeline.
  const scaleMlp = key === 'mlp' && !load.vectorize
  const imports = [
    ...load.imports,
    'from sklearn.model_selection import train_test_split',
    ...(load.vectorize ? ['from sklearn.feature_extraction.text import CountVectorizer', 'from sklearn.pipeline import make_pipeline'] : []),
    ...(scaleMlp ? ['from sklearn.preprocessing import StandardScaler', 'from sklearn.pipeline import make_pipeline'] : []),
    m.imp,
    'from sklearn.dummy import DummyClassifier',
  ].join('\n')
  const step4 = load.vectorize
    ? `# 4. Turn the text into word-count features, then train the model
#    (the vectorizer is inside the pipeline, so it learns only from the training set)
model = make_pipeline(CountVectorizer(), ${m.ctor})
model.fit(X_train, y_train)`
    : scaleMlp
    ? `# 4. Scale the features, then train the neural network
model = make_pipeline(StandardScaler(), ${m.ctor})
model.fit(X_train, y_train)`
    : `# 4. Run the model (train it on the training set)
model = ${m.ctor}
model.fit(X_train, y_train)`
  return `# Imports
${imports}

${load.body}

# 3. Split into training and validation sets
X_train, X_val, y_train, y_val = train_test_split(X, y, test_size=0.2, random_state=0)

${step4}

# 5. Get the results (score on the held-out validation set)
print("Accuracy:", round(model.score(X_val, y_val), 3))

# Baseline: always guess the most common class
dummy = DummyClassifier(strategy="most_frequent").fit(X_train, y_train)
print("Baseline:", round(dummy.score(X_val, y_val), 3))`
}
